"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import VideoPlayer from "../video/VideoPlayer";
import MediaViewer from "../common/MediaViewer";
import ImageLoader from "../common/ImageLoader";

const Carousel = ({
  media,
  enablePreview = true,
  autoscroll = false,
  hideThumb = false,
  scrollInterval = 4000,
  inMarketPlace = false,
  imgRes = "500",
  zoomImgRes = "800",
  customHeight = "100%",
  inMainMarketPlace = false,
}) => {
  // helpers to rewrite urls
  const getHighRes = (url) =>
    url
      ?.replace?.("r200x200", `r${imgRes}x${imgRes}`)
      ?.replace?.("v200x200", `v${imgRes}x${imgRes}`);
  const getZoomRes = (url) =>
    url
      ?.replace?.("r200x200", `r${zoomImgRes}x${zoomImgRes}`)
      ?.replace?.("v200x200", `v${zoomImgRes}x${zoomImgRes}`);

  // state & refs
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [lastMediaKey, setLastMediaKey] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const userInitiatedRef = useRef(false);
  const mainStripRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const intersectionRef = useRef(null);

  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchCurrentXRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const isSwipingRef = useRef(false);

  const thumbsRef = useRef([]);
  const mainRef = useRef([]);

  // scroll sync helpers
  const scrollTimeoutRef = useRef(null);
  const programmaticScrollRef = useRef(false);
  const selectedIndexRef = useRef(0);

  // keep selectedIndexRef in sync
  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  // normalize incoming media list
  const validMedia = useMemo(
    () =>
      (media || [])
        .filter((item) => item && (item.mediaThumb || item.mediaUrl))
        .map((item) => {
          const baseUrl =
            item?.mediaThumb && typeof item.mediaThumb === "string"
              ? item.mediaThumb
              : item.mediaUrl;
          const highResUrl = getHighRes(baseUrl);
          const zoomUrl = getZoomRes(baseUrl);
          return {
            ...item,
            highResUrl,
            zoomUrl: zoomUrl !== highResUrl ? zoomUrl : undefined,
          };
        }),
    [media],
  );

  // Reset when media changes (hooks remain top-level)
  useEffect(() => {
    setSelectedIndex(0);
    setImagesLoaded(false);
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
    // update key for media change detection
    const key = JSON.stringify(
      (media || []).map((m) => m?.mediaThumb || m?.mediaUrl),
    );
    if (key !== lastMediaKey) setLastMediaKey(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  // IntersectionObserver — top-level hook, but effect checks for node inside
  useEffect(() => {
    const node = mainStripRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    intersectionRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0);
        });
      },
      { threshold: [0, 0.25, 0.5, 1] },
    );

    intersectionRef.current.observe(node);

    return () => {
      if (intersectionRef.current && node)
        intersectionRef.current.unobserve(node);
      intersectionRef.current = null;
    };
  }, []);

  // helper to center element
  const scrollToIndex = (index, behavior = "smooth") => {
    const container = mainStripRef.current;
    const el = mainRef.current[index];
    if (!container || !el) return;
    const containerCenter = container.clientWidth / 2;
    const elCenter = el.offsetLeft + el.clientWidth / 2;
    const newScrollLeft = Math.max(0, elCenter - containerCenter);

    programmaticScrollRef.current = true;
    container.scrollTo({ left: newScrollLeft, behavior });

    window.clearTimeout(scrollTimeoutRef.current);
    // clear guard shortly after smooth scroll should finish
    scrollTimeoutRef.current = window.setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 300);
  };

  // Swiping helpers (top-level hooks not conditional)
  const beginSwipe = (touchX, touchY) => {
    const container = mainStripRef.current;
    if (!container) return;
    isSwipingRef.current = false;
    touchStartXRef.current = touchX;
    touchStartYRef.current = touchY;
    touchCurrentXRef.current = touchX;
    startScrollLeftRef.current = container.scrollLeft;
  };

  const moveSwipe = (touchX, touchY) => {
    const container = mainStripRef.current;
    if (!container) return;
    const dx = touchX - touchStartXRef.current;
    const dy = touchY - touchStartYRef.current;

    if (!isSwipingRef.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dx) > Math.abs(dy)) isSwipingRef.current = true;
      else {
        isSwipingRef.current = false;
        return;
      }
    }

    touchCurrentXRef.current = touchX;
    container.scrollLeft =
      startScrollLeftRef.current - (touchX - touchStartXRef.current);
  };

  const endSwipe = () => {
    const container = mainStripRef.current;
    if (!container) return;

    if (!isSwipingRef.current) {
      scrollToIndex(selectedIndex, "smooth");
      return;
    }

    const dx = touchCurrentXRef.current - touchStartXRef.current;
    const threshold = Math.max(50, container.clientWidth * 0.08);

    if (dx > threshold) {
      // swipe right -> previous
      if (selectedIndex === 0)
        requestAnimationFrame(() => scrollToIndex(0, "smooth"));
      else {
        userInitiatedRef.current = true;
        setSelectedIndex((prev) => {
          const next = prev - 1;
          requestAnimationFrame(() => scrollToIndex(next, "smooth"));
          return next;
        });
      }
    } else if (dx < -threshold) {
      // swipe left -> next
      if (selectedIndex === validMedia.length - 1)
        requestAnimationFrame(() => scrollToIndex(selectedIndex, "smooth"));
      else {
        userInitiatedRef.current = true;
        setSelectedIndex((prev) => {
          const next = prev + 1;
          requestAnimationFrame(() => scrollToIndex(next, "smooth"));
          return next;
        });
      }
    } else {
      scrollToIndex(selectedIndex, "smooth");
    }

    isSwipingRef.current = false;
  };

  // thumbnail scrolling sync (top-level)
  useEffect(() => {
    if (hideThumb) return;
    if (!userInitiatedRef.current) return;
    const el = thumbsRef.current[selectedIndex];
    if (el)
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    userInitiatedRef.current = false;
  }, [selectedIndex, hideThumb]);

  // programmatic scroll when selectedIndex changes
  useEffect(() => {
    if (!userInitiatedRef.current) {
      // when selectedIndex changes programmatically (e.g. dot click) we want to center
      requestAnimationFrame(() => scrollToIndex(selectedIndex));
    } else {
      // clear the user initiated flag after we handled it
      userInitiatedRef.current = false;
    }
  }, [selectedIndex]);

  // auto scroll helpers
  const startAutoScrollInterval = (intervalMs = scrollInterval) => {
    stopAutoScrollInterval();
    if (
      !autoscroll ||
      validMedia.length <= 1 ||
      !isVisible ||
      isHovered ||
      !imagesLoaded
    )
      return;
    autoScrollIntervalRef.current = setInterval(() => {
      userInitiatedRef.current = true;
      setSelectedIndex((prev) => {
        const next = prev >= validMedia.length - 1 ? 0 : prev + 1;
        requestAnimationFrame(() => scrollToIndex(next));
        return next;
      });
    }, intervalMs);
  };
  const stopAutoScrollInterval = () => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };
  const restartAutoScrollInterval = () => {
    if (!autoscroll) return;
    stopAutoScrollInterval();
    autoScrollIntervalRef.current = setTimeout(
      () => startAutoScrollInterval(scrollInterval),
      scrollInterval,
    );
  };

  // wait for images to load (top-level effect)
  useEffect(() => {
    const node = mainStripRef.current;
    if (!node) {
      setImagesLoaded(true);
      return;
    }

    const imgs = Array.from(node.querySelectorAll("img"));
    if (imgs.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let pending = imgs.filter((img) => !img.complete).length;
    if (pending === 0) {
      setImagesLoaded(true);
      return;
    }

    setImagesLoaded(false);
    const onLoadOrError = () => {
      pending -= 1;
      if (pending <= 0) setImagesLoaded(true);
    };

    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", onLoadOrError);
        img.addEventListener("error", onLoadOrError);
      }
    });

    return () => {
      imgs.forEach((img) => {
        img.removeEventListener("load", onLoadOrError);
        img.removeEventListener("error", onLoadOrError);
      });
    };
  }, [validMedia]);

  // autoscroll effect (top-level)
  useEffect(() => {
    if (!autoscroll || !isVisible || isHovered || validMedia.length <= 1) {
      stopAutoScrollInterval();
      return;
    }
    if (!imagesLoaded) {
      stopAutoScrollInterval();
      return;
    }
    startAutoScrollInterval(scrollInterval);
    return () => stopAutoScrollInterval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoscroll,
    validMedia.length,
    isVisible,
    isHovered,
    scrollInterval,
    imagesLoaded,
  ]);

  // sync selectedIndex to user scroll (touch/trackpad/wheel)
  useEffect(() => {
    const container = mainStripRef.current;
    if (!container) return;

    const onScroll = () => {
      if (programmaticScrollRef.current) return;

      window.clearTimeout(scrollTimeoutRef.current);
      // when the user stops scrolling for 80ms, evaluate the closest element
      scrollTimeoutRef.current = window.setTimeout(() => {
        const scrollLeft = container.scrollLeft;
        const containerCenter = scrollLeft + container.clientWidth / 2;

        let closestIndex = 0;
        let minDist = Infinity;
        for (let i = 0; i < mainRef.current.length; i++) {
          const el = mainRef.current[i];
          if (!el) continue;
          const elCenter = el.offsetLeft + el.clientWidth / 2;
          const dist = Math.abs(elCenter - containerCenter);
          if (dist < minDist) {
            minDist = dist;
            closestIndex = i;
          }
        }

        if (closestIndex !== selectedIndexRef.current) {
          // mark as user-initiated so thumbnails update without fighting the snap
          userInitiatedRef.current = true;
          setSelectedIndex(closestIndex);

          // after setting the index, snap the closest element to the center
          // scrollToIndex sets programmaticScrollRef.current to true to prevent loops
          requestAnimationFrame(() => {
            scrollToIndex(closestIndex, "smooth");
          });
        }
      }, 80); // debounce
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      window.clearTimeout(scrollTimeoutRef.current);
    };
  }, []); // attach once

  // touch handlers that call swipe helpers
  const handleTouchStart = (e) => {
    if (!e.touches || !e.touches.length) return;
    const t = e.touches[0];
    beginSwipe(t.clientX, t.clientY);
  };
  const handleTouchMove = (e) => {
    if (!e.touches || !e.touches.length) return;
    const t = e.touches[0];
    moveSwipe(t.clientX, t.clientY);
  };
  const handleTouchEnd = () => endSwipe();

  const onThumbClick = (index) => {
    userInitiatedRef.current = true;
    setSelectedIndex(index);
    requestAnimationFrame(() => scrollToIndex(index));
    restartAutoScrollInterval();
  };

  const onThumbHover = (index) => {
    userInitiatedRef.current = true;
    setSelectedIndex(index);
    requestAnimationFrame(() => scrollToIndex(index));
    restartAutoScrollInterval();
  };

  const onDotClick = (index) => {
    userInitiatedRef.current = true;
    setSelectedIndex(index);
    requestAnimationFrame(() => scrollToIndex(index));
    restartAutoScrollInterval();
  };

  // Render — we can safely return null if no media, hooks already ran above
  if (!validMedia.length) return null;

  return (
    <>
      <div
        className="gallery-container"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="main-image-wrapper">
          {validMedia.length > 1 && !autoscroll && (
            <div
              className="modal-arrow-div left"
              onClick={() => {
                // Prev still wraps when using arrows
                const newIndex =
                  selectedIndex === 0
                    ? validMedia.length - 1
                    : selectedIndex - 1;
                userInitiatedRef.current = true;
                setSelectedIndex(newIndex);
                requestAnimationFrame(() => scrollToIndex(newIndex));
                restartAutoScrollInterval();
              }}
            >
              <button className="modal-arrow left">&#10094;</button>
            </div>
          )}

          <div
            className="media-main-strip main-modal"
            ref={mainStripRef}
            style={{
              display: "flex",
              overflowX: "auto",
              overflowY: "hidden",
              scrollBehavior: "smooth",
              alignItems: "center",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              gap: "1rem",
              height: customHeight,
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            {validMedia.map((item, index) => (
              <div
                key={item._id || `${index}-${item.mediaName}`}
                ref={(el) => (mainRef.current[index] = el)}
                className={`media-main-item ${
                  index === selectedIndex ? "active" : ""
                } ${item.mediaType === "video" ? "media-video-item" : ""} ${
                  inMarketPlace ? "mediaInMarket" : ""
                }`}
                onClick={() => setShowMediaViewer(true)}
              >
                {item.mediaType === "image" && (
                  <ImageLoader
                    media={item}
                    zoom={true}
                    alt={item.name}
                    skeletonStyle={{
                      width: "100%",
                      borderRadius: "8px",
                      height: "30rem",
                    }}
                  />
                )}

                {item.mediaType === "video" && (
                  <VideoPlayer
                    src={item.mediaUrl}
                    className="media-main-video"
                    skeletonStyle={{ width: "100%", minHeight: "30rem" }}
                    autoPlay={true}
                    continuousPlay={true}
                  />
                )}

                {item.mediaType === "file" && (
                  <ImageLoader
                    media={item}
                    alt={item.name}
                    skeletonStyle={{
                      width: "100%",
                      borderRadius: "8px",
                      height: "30rem",
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          {validMedia.length > 1 && !autoscroll && (
            <div
              className="modal-arrow-div right"
              onClick={() => {
                const newIndex =
                  selectedIndex === validMedia.length - 1
                    ? 0
                    : selectedIndex + 1;
                userInitiatedRef.current = true;
                setSelectedIndex(newIndex);
                requestAnimationFrame(() => scrollToIndex(newIndex));
                restartAutoScrollInterval();
              }}
            >
              <button className="modal-arrow right">&#10095;</button>
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {validMedia.length > 1 && !hideThumb && (
          <div className="image-gallery-wrapper">
            <div className="image-gallery">
              {validMedia.map((item, index) => (
                <div
                  key={item._id || `${index}-${item.mediaName}`}
                  ref={(el) => (thumbsRef.current[index] = el)}
                  role="button"
                  aria-label={`View image ${index + 1}`}
                  className={`gallery-item ${
                    index === selectedIndex ? "active-thumbnail" : ""
                  }`}
                  onClick={() => onThumbClick(index)}
                  onMouseEnter={() => onThumbHover(index)}
                >
                  <ImageLoader
                    media={item}
                    skeletonHeight={60}
                    skeletonWidth={60}
                    alt={item.name}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tracking dots */}
        {validMedia.length > 1 &&
          hideThumb &&
          !inMarketPlace &&
          !inMainMarketPlace && (
            <div
              className="carousel-dots"
              role="tablist"
              aria-label="Image gallery navigation"
              onClick={(e) => e.stopPropagation()}
            >
              {validMedia.map((_, i) => (
                <button
                  key={`dot-${i}`}
                  className={`carousel-dot ${
                    i === selectedIndex ? "active" : ""
                  }`}
                  aria-label={`Go to image ${i + 1}`}
                  aria-pressed={i === selectedIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDotClick(i);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onDotClick(i);
                    }
                  }}
                  style={{
                    background: i === selectedIndex ? "#0e86d4" : "#bdbdbd",
                  }}
                />
              ))}
            </div>
          )}
      </div>

      {showMediaViewer && enablePreview && (
        <MediaViewer
          media={validMedia}
          setShowMediaViewer={setShowMediaViewer}
        />
      )}
    </>
  );
};

export default Carousel;
